/**
 * Saving a file the API produced.
 *
 * The API authenticates with a bearer token held in memory, so an `<a href>`
 * pointing at it carries no credentials and simply 401s. Every download is
 * therefore fetched through the SDK — which attaches the token and refreshes it
 * when needed — and handed to the browser as an object URL.
 */

export async function saveFile(
  fetcher: () => Promise<{ blob: Blob; filename: string | null }>,
  fallbackName: string,
): Promise<void> {
  const { blob, filename } = await fetcher();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename ?? fallbackName;
  document.body.append(link);
  link.click();
  link.remove();

  // Released on the next tick: revoking synchronously can cancel the download
  // in some browsers before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Opens a file the API produced in a new tab — a label to print (ADR-068).
 *
 * The tab is opened before the fetch, inside the click, so a popup blocker
 * lets it through; it is filled when the file arrives. Blocked anyway, the
 * file is saved instead.
 */
export async function openFile(
  fetcher: () => Promise<{ blob: Blob; filename: string | null }>,
  fallbackName: string,
): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const { blob, filename } = await fetcher();
    if (!tab) {
      await saveFile(async () => ({ blob, filename }), fallbackName);
      return;
    }
    const url = URL.createObjectURL(blob);
    tab.location.href = url;
    // Long enough for the tab to read it; it holds its own copy once loaded.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/** Saves data the store already holds as a JSON file — a customer's export (R10). */
export async function saveJson(data: unknown, filename: string): Promise<void> {
  await saveFile(
    async () => ({ blob: new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), filename }),
    filename,
  );
}
